import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000ComboBoxBase from "../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import U2000AutoCompleteInput from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { U3129ProjectSearchPanel } from "../../../u3129Timesheet/components/form/U3129LineProjectSubprojectRowCells";
import { useU3141FindProjectHandlers } from "../../../u3141LandRegister/U3141UseFindProjectHandlers";
import type { U3141FindProjectItem } from "../../../u3141LandRegister/U3141Service";
import { FaUndoAlt, FaPlus } from "react-icons/fa";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import {
  U3142_LOT_DETAIL_DOCUMENT_TYPES,
  U3142_DOCUMENT_UPLOAD_MAX_FILES,
  luHandleAppiError,
  type U3142LotDetailDocument,
} from "../../U3142Service";
import { useU3142AddDocumentHandlers } from "./U3142UseDocumentsHandlers";
import { U3141DocumentPdfFilesQueueUpload } from "../../../u3141LandRegister/components/form/u3141ChildLotChain/U3141DocumentPdfFilesQueueUpload";
import { u3141ValidateDocumentUploadFile } from "../../../u3141LandRegister/tools/u3141DocumentUploadFileQueue";
import { NS } from "../../U3142";

/** Types pour lesquels projectNumber est obligatoire (backend rejette si absent). */
const U3142_DOCUMENT_TYPES_REQUIRING_PROJECT_NUMBER = [
  "rfLotPlansCorrectionCadastrale",
  "rfLotLivreRenvoi",
] as const;

/** Classes des boutons alignées sur U2000SaveCancelDialog (formulaire). */
const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
  active:bg-gray-100 active:scale-95
  disabled:cursor-not-allowed disabled:opacity-60`;
const primaryButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2
  active:bg-green-800 active:scale-95
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

export interface U3142AddDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  lotId: string | number;
  /** Documents déjà présents (validation numéro de projet par type). */
  existingDocuments: U3142LotDetailDocument[];
  overlayClassName?: string;
}

type DocumentTypeOption = { key: string; description: string };

type U3142AddDocumentProjectForm = {
  selectedProject: U3141FindProjectItem | null;
  projectNumber: string;
};

const emptyU3142AddDocumentProjectForm = (): U3142AddDocumentProjectForm => ({
  selectedProject: null,
  projectNumber: "",
});

export const U3142AddDocumentDialog: React.FC<U3142AddDocumentDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  lotId,
  existingDocuments = [],
  overlayClassName,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [projectFormData, setProjectFormData] = useState<U3142AddDocumentProjectForm>(emptyU3142AddDocumentProjectForm);
  const [submitting, setSubmitting] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  const findProjectHandlers = useU3141FindProjectHandlers();

  const { createDocument } = useU3142AddDocumentHandlers({
    lotId,
  });

  const projectNumber = projectFormData.projectNumber;

  const getExistingProjectNumbersForType = useCallback(
    (type: string) =>
      existingDocuments
        .filter((d) => d.documentType === type)
        .map((d) => (d.projectNumber ?? d.project?.projectNumber ?? "").trim())
        .filter(Boolean),
    [existingDocuments],
  );

  const documentTypeOptions: DocumentTypeOption[] = useMemo(
    () =>
      U3142_LOT_DETAIL_DOCUMENT_TYPES.map((type) => ({
        key: type,
        description: t(`lblDocumentType.${type}`, { ns: NS }) || type,
      })),
    [t],
  );

  const selectedTypeOption = useMemo(
    () => (documentType ? documentTypeOptions.find((o) => o.key === documentType) ?? null : null),
    [documentType, documentTypeOptions],
  );

  const requiresProjectNumber =
    documentType !== null &&
    U3142_DOCUMENT_TYPES_REQUIRING_PROJECT_NUMBER.includes(
      documentType as (typeof U3142_DOCUMENT_TYPES_REQUIRING_PROJECT_NUMBER)[number],
    );

  const projectFormStateHandlers = useMemo(() => {
    const getData = () => projectFormData;
    const setData = (upd: Partial<U3142AddDocumentProjectForm>) =>
      setProjectFormData((prev) => ({ ...prev, ...upd }));
    return {
      getData,
      setData,
      getTouched: () => ({}),
      getErrors: () => ({}),
      getFormState: () => U2000FormStateEnumState.EDIT,
      isSubmitted: () => false,
      getOtherErrors: () => undefined,
      setOtherErrors: () => {},
      setErrors: () => {},
      setTouched: () => {},
      onChange: () => {},
    };
  }, [projectFormData]);

  const onSearchProject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => findProjectHandlers.search(criterias),
    [findProjectHandlers],
  );

  const extractProject = useCallback((result: U3141FindProjectItem | null): { fieldName: string; value: unknown }[] => {
    if (result) {
      return [
        { fieldName: "selectedProject", value: result },
        { fieldName: "projectNumber", value: result.projetNumber ?? "" },
      ];
    }
    return [
      { fieldName: "selectedProject", value: null },
      { fieldName: "projectNumber", value: "" },
    ];
  }, []);

  useEffect(() => {
    if (isOpen) {
      setDocumentType(null);
      setFiles([]);
      setProjectFormData(emptyU3142AddDocumentProjectForm());
      setSubmitting(false);
      setFormResetKey((k) => k + 1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (documentType && !documentTypeOptions.some((o) => o.key === documentType)) {
      setDocumentType(null);
    }
  }, [documentType, documentTypeOptions]);

  useEffect(() => {
    if (!requiresProjectNumber) {
      setProjectFormData(emptyU3142AddDocumentProjectForm());
    }
  }, [requiresProjectNumber]);

  const handleFilesChange = useCallback((next: File[]) => {
    setFiles(next);
  }, []);

  const handleSubmit = async () => {
    if (!documentType || files.length === 0) return;

    for (const file of files) {
      const validationKey = u3141ValidateDocumentUploadFile(file);
      if (validationKey) {
        toast.error(t(validationKey, { ns: NS }));
        return;
      }
    }

    const projectNumberTrimmed = projectNumber?.trim() ?? "";
    if (requiresProjectNumber && !projectNumberTrimmed) {
      toast.error(t("errProjectNumberRequiredForDocumentType", { ns: NS }));
      return;
    }
    if (requiresProjectNumber && projectNumberTrimmed) {
      const existingForType = getExistingProjectNumbersForType(documentType);
      if (existingForType.includes(projectNumberTrimmed)) {
        toast.error(t("errProjectNumberAlreadyUsedForDocumentType", { ns: NS }));
        return;
      }
    }

    setSubmitting(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("documentType", documentType);
        formData.append("file", file);
        if (projectNumberTrimmed) {
          formData.append("projectNumber", projectNumberTrimmed);
        }
        await createDocument(formData);
      }
      if (files.length === 1) {
        toast.success(t("msgDocumentAdded", { ns: NS }));
      } else {
        toast.success(t("msgDocumentsAddedCount", { ns: NS, count: files.length }));
      }
      setDocumentType(null);
      setFiles([]);
      setProjectFormData(emptyU3142AddDocumentProjectForm());
      onSuccess();
      onClose();
    } catch (error: unknown) {
      setSubmitting(false);
      const err = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, NS, luHandleAppiError);
      if (err.genericErrors?.length) err.genericErrors.forEach((e) => toast.error(e.text, e.title));
    }
  };

  const isValid =
    !!documentType &&
    files.length > 0 &&
    (!requiresProjectNumber || (projectNumber?.trim()?.length ?? 0) > 0);

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => onClose()}
      title={t("dlgAddDocumentTitle", { ns: NS })}
      width="large"
      height="max-h-[90vh]"
      contentOverflowVisible
      overlayClassName={overlayClassName}
    >
      <div className="flex h-full flex-col focus:outline-hidden">
        <section className="min-h-0 flex-1 overflow-visible px-6 py-4">
          <div className="flex flex-col gap-4">
            <U3141DocumentPdfFilesQueueUpload
              key={formResetKey}
              i18nNs={NS}
              label={t("lblDocumentFile", { ns: NS })}
              files={files}
              onChange={handleFilesChange}
              maxFiles={U3142_DOCUMENT_UPLOAD_MAX_FILES}
              allowsMultipleFiles
              disabled={submitting}
              formResetKey={formResetKey}
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{t("lblDocumentType", { ns: NS })}</label>
              {files.length > 0 && !documentType ? (
                <p className="mb-2 text-xs text-amber-800">{t("msgDocumentUploadSelectTypeToSubmit", { ns: NS })}</p>
              ) : null}
              <U2000ComboBoxBase<DocumentTypeOption>
                keyField="key"
                descField="description"
                data={documentTypeOptions}
                value={selectedTypeOption}
                onChangeObject={(o) => setDocumentType(o?.key ?? null)}
                placeHolder={t("lblDocumentTypePlaceholder", { ns: NS })}
                noneItemLabel={t("lblNoneSelection", { ns: NS })}
              />
            </div>

            {requiresProjectNumber ? (
              <U2000AutoCompleteInput
                pageSize={25}
                formStateHandlers={projectFormStateHandlers}
                fieldName="selectedProject"
                mainFieldName="projetNumber"
                entityLink="U3121"
                label={t("lblProjectNumber", { ns: NS })}
                required
                onSearch={onSearchProject}
                displayFn={(item) => {
                  const p = item as U3141FindProjectItem | null;
                  return p ? (p.projetNumber ?? p.id ?? "") : "";
                }}
                overrideExtractDataFn={(item) => extractProject(item as U3141FindProjectItem | null)}
                basicResultHeight={300}
                resultLabels={[]}
                resultFields={["projetNumber", "projetName"]}
                minCarLaunchSearch={3}
                customSearchPanelHeight={500}
                panelWidth={800}
                dropdownWidth={400}
                renderPanel={(props) => <U3129ProjectSearchPanel {...props} ns="u3141" />}
                readonly={submitting}
              />
            ) : null}
          </div>
        </section>

        <footer className="flex shrink-0 items-center justify-end gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => void handleSubmit()}
            disabled={!isValid || submitting}
            aria-label={t("dlgLblAdd", { ns: NS })}
          >
            <FaPlus className="h-4 w-4" />
            <span>{t("dlgLblAdd", { ns: NS })}</span>
          </button>
          <button
            type="button"
            className={cancelButtonClass}
            onClick={() => onClose()}
            disabled={submitting}
            aria-label={t("dlgLblCancel", { ns: NS })}
          >
            <FaUndoAlt className="h-4 w-4" />
            <span>{t("dlgLblCancel", { ns: NS })}</span>
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
